import { useState, useMemo } from 'react';
import { useForm, useFieldArray, useWatch, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  Button,
  Input,
  Label,
  Switch,
  Select,
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogContent,
  DialogFooter,
  Alert,
  HelpTip,
  useConfirmDialog,
  useToast,
} from '@/components/ui';
import { userFormSchema, type UserFormValues } from '@/lib/zod-schemas';
import {
  useUsers,
  useUserActions,
  useDepartments,
  useProfiles,
  useSegmentosClientes,
  useVendedoresOmie,
} from '@/lib/admin-store';
import type { User, RoleType, ProfileSlug } from '@/lib/admin-types';
import { ROLES_LABELS, PROFILE_SLUG_LABELS } from '@/lib/admin-types';
// BF.5 / FEAT-V2-015: copiar aspectos adicionais via endpoint backend.
import { api } from '@/lib/api';
// UI-2 / TD-V2-PERM-02: seção "Permissões" no edit.
import {
  UserPermissoesSection,
  type UserPermissoesValue,
} from './components/UserPermissoesSection';
// TD-V2-UX-01 (2026-05-26): copy-from notification preferences movido pra admin.
import { UserNotificationCopySection } from './components/UserNotificationCopySection';
// C3 (rodada AJUDA-UX-QA): inativar colaborador do comercial com carteira
// abre o wizard de offboarding (decide o destino de leads/propostas/clientes).
import { type CarteiraResumo } from '@/lib/crm/carteira-store';
import { OffboardingCarteiraWizard } from './OffboardingCarteiraWizard';

type CopyAspect = 'atividades' | 'tipos_atividade' | 'agenda' | 'preferencias';

const COPY_ASPECT_LABELS: Record<CopyAspect, string> = {
  atividades: 'Atividades pendentes',
  tipos_atividade: 'Tipos de atividade (visibilidade)',
  agenda: 'Agenda padrão (vista preferida)',
  preferencias: 'Preferências UI',
};

interface UserFormDialogProps {
  open: boolean;
  /** Se passar, é edição. Se null/undefined, é criação. */
  user?: User | null;
  onClose: () => void;
}

/**
 * Modal de criar/editar usuário.
 *
 * Form com validação Zod + React Hook Form (industry standard).
 * Estados: loading no submit, erro de validação inline, success implícito (fecha modal).
 *
 * Robustez:
 *   - Validação client-side completa antes de submeter
 *   - Mensagens de erro em pt-BR contextualizadas
 *   - Lock do form durante submit (evita double-submit)
 *   - Reset automático ao abrir/fechar
 *   - Acessibilidade via labels associados
 */
export function UserFormDialog({ open, user, onClose }: UserFormDialogProps) {
  const isEdit = !!user;
  const departments = useDepartments();
  const profiles = useProfiles();
  const allUsers = useUsers();
  const { createUser, updateUser, pending } = useUserActions();

  // CA4 (Migration 406): "Acesso ao cofre por segmento". Lista vazia = todos os
  // segmentos (padrão preservado). Segmentos disponíveis = tags de cliente.
  const segmentosDisponiveis = useSegmentosClientes();
  const [cofreSegmentos, setCofreSegmentos] = useState<string[]>(user?.cofreSegmentos ?? []);

  // CARD-VENDAS-VENDEDOR (Migration 491): vínculo com o vendedor do Omie —
  // com ele, a Home da pessoa ganha o card "Minhas vendas" do mês. Estado
  // local (como profileId/cofreSegmentos); '' no Select = sem vínculo (null).
  const vendedoresOmie = useVendedoresOmie();
  const [omieVendedorCodigo, setOmieVendedorCodigo] = useState<string | null>(
    user?.omieVendedorCodigo ?? null,
  );

  /**
   * 🔴 O WHATSAPP DA PESSOA (#269, mig 590) — e o uso é UM SÓ: o bot ignora as
   * mensagens dela nos grupos.
   *
   * ⚠️ **Fica na ficha, e não numa lista de números, por um motivo prático:**
   * quando alguém sai da empresa, o número sai junto. Lista paralela envelhece
   * e ninguém lembra de limpar — e um número velho na lista de "ignorar" faz o
   * bot calar para o morador que herdou a linha.
   *
   * ❌ **Não é obrigatório.** No dia em que isto sobe ninguém tem número
   * cadastrado, e ficha em branco não pode quebrar o descarte.
   */
  const [telefoneWhatsapp, setTelefoneWhatsapp] = useState<string>(
    user?.telefoneWhatsapp ?? '',
  );
  function toggleCofreSegmento(seg: string) {
    setCofreSegmentos((prev) =>
      prev.includes(seg) ? prev.filter((s) => s !== seg) : [...prev, seg],
    );
  }

  // FEAT-V2-037 Fase A: seletor de perfil + validação combo super-admin.
  // Estado local porque o profileId precisa interagir com isAdmin (ambos
  // controlados pelo react-hook-form mas decisão UX: profile ao lado de
  // isAdmin no toggle group, ambos no header do form). Mantemos `profileId`
  // como uncontrolled select sincronizado via state extra (simétrico ao
  // padrão do `permissoes` mais abaixo no form).
  // null = super-admin (só permitido se isAdmin checkbox marcado).
  const [profileId, setProfileId] = useState<string | null>(
    user?.profileId ?? 'p_administrativo',
  );

  // Pra "Copiar departamentos de" — não inclui o próprio usuário sendo editado
  const otherUsers = useMemo(
    () => allUsers.filter((u) => !user || u.id !== user.id),
    [allUsers, user]
  );
  const [copyFromUserId, setCopyFromUserId] = useState('');
  // BF.5 / FEAT-V2-015 — copiar aspectos via backend (só em edit; criação
  // primeiro cria o user, depois admin pode reabrir pra copiar).
  const [copyAspects, setCopyAspects] = useState<Set<CopyAspect>>(new Set());
  const [copyAspectsSource, setCopyAspectsSource] = useState('');
  // FASE-C: o resultado deixou de carregar "✓" dentro do texto (símbolo como
  // ícone + string usada como flag de sucesso). Agora o estado é explícito.
  const [copyAspectsResult, setCopyAspectsResult] = useState<
    { ok: boolean; texto: string } | null
  >(null);
  const [copyAspectsPending, setCopyAspectsPending] = useState(false);

  async function handleCopyAspects() {
    if (!user || !copyAspectsSource || copyAspects.size === 0) return;
    setCopyAspectsPending(true);
    setCopyAspectsResult(null);
    try {
      const res = await api.post<{ counts: Record<string, number> }>(
        `/api/v1/users/${encodeURIComponent(user.id)}/copy-from/${encodeURIComponent(copyAspectsSource)}`,
        { aspects: Array.from(copyAspects) },
      );
      const parts = Object.entries(res.counts)
        .filter(([, n]) => n > 0)
        .map(([k, n]) => `${n} ${k}`);
      setCopyAspectsResult({
        ok: true,
        texto: parts.length > 0 ? `Copiado: ${parts.join(', ')}` : 'Não havia nada pra copiar.',
      });
      setCopyAspects(new Set());
      setCopyAspectsSource('');
    } catch (e) {
      setCopyAspectsResult({
        ok: false,
        texto:
          e instanceof Error
            ? `Não foi possível copiar. ${e.message}`
            : 'Não foi possível copiar. Tente de novo.',
      });
    } finally {
      setCopyAspectsPending(false);
    }
  }

  function toggleAspect(a: CopyAspect) {
    setCopyAspects((prev) => {
      const next = new Set(prev);
      if (next.has(a)) next.delete(a);
      else next.add(a);
      return next;
    });
  }
  const [copiedFromName, setCopiedFromName] = useState<string | null>(null);

  // UI-2 / TD-V2-PERM-02 + FEAT-V2-035 + FEAT-V2-036: state local da seção
  // Permissões. Default de podeVerTarefasOutros = true (Migration 059) — só
  // vira false quando admin explicitamente revoga.
  const [permissoes, setPermissoes] = useState<UserPermissoesValue>(() => ({
    podeCriarTarefaPraOutros: user?.podeCriarTarefaPraOutros ?? false,
    podeAgendarSemAprovacao: user?.podeAgendarSemAprovacao ?? false,
    aprovadorId: user?.aprovadorId ?? null,
    podeInativarCards: user?.podeInativarCards ?? false,
    // FEAT-V2-036: default true preserva semântica conservadora do migration default.
    podeVerTarefasOutros: user?.podeVerTarefasOutros ?? true,
  }));

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<UserFormValues>({
    resolver: zodResolver(userFormSchema),
    defaultValues: user
      ? {
          nome: user.nome,
          email: user.email,
          isAdmin: user.isAdmin,
          ativo: user.ativo,
          isVendedor: user.isVendedor ?? false,
          departamentos: user.departamentos,
        }
      : {
          nome: '',
          email: '',
          isAdmin: false,
          ativo: true,
          isVendedor: false,
          departamentos: [],
        },
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'departamentos',
  });

  // INDICADORES-REDESIGN (Migration 382): o switch "Vendedor" só faz sentido
  // pra quem tem o departamento comercial. Recalcula AO VIVO — useWatch reflete
  // troca de Select num item existente, não só append/remove (que `fields`
  // pega). Mesmo predicado do `eraComercial` mais abaixo.
  const departamentosAtuais = useWatch({ control, name: 'departamentos' });
  const temComercial = (departamentosAtuais ?? fields).some(
    (d) => d?.departmentSlug === 'comercial',
  );

  // CA4: a seção de segmento do cofre só faz sentido pra quem acessa o cofre —
  // dept manutencao ou projetos (admin bypassa tudo). Recalcula ao vivo.
  const isAdminAtual = useWatch({ control, name: 'isAdmin' });
  const temCofreDept = (departamentosAtuais ?? fields).some(
    (d) => d?.departmentSlug === 'manutencao' || d?.departmentSlug === 'projetos',
  );

  /** Copia os departamentos do usuário selecionado pro form atual. */
  function handleCopyDepartments() {
    const source = otherUsers.find((u) => u.id === copyFromUserId);
    if (!source) return;
    // useFieldArray.replace substitui o array inteiro
    replace(source.departamentos.map((d) => ({ ...d })));
    setCopiedFromName(source.nome);
    setCopyFromUserId('');
    // Limpa o feedback após 3s
    setTimeout(() => setCopiedFromName(null), 3000);
  }

  // C3: wizard de offboarding da carteira (inativação de comercial).
  const [offboardingAberto, setOffboardingAberto] = useState(false);
  const confirmar = useConfirmDialog();
  const toast = useToast();

  async function onSubmit(values: UserFormValues) {
    // UI-2: anexa permissões ao payload PUT. Schema Zod ignora propriedades extras
    // não declaradas (passa pro backend que aceita campos extras opcionais).
    // FEAT-V2-037 Fase A: anexa profileId. null = super-admin (só vai pro
    // backend se isAdmin=true; backend valida combo).
    // G5b (hardening): o switch "Vendedor" só existe com departamento comercial.
    // Se o admin tirou o comercial, o switch some da UI mas o valor podia ficar
    // `true` órfão no form — normaliza pra false antes de persistir.
    const temComercialNoSubmit = values.departamentos.some(
      (d) => d.departmentSlug === 'comercial',
    );
    const isVendedorLimpo = temComercialNoSubmit ? values.isVendedor : false;
    // CA4: persiste os segmentos de cofre (vazio = todos). Sempre enviado — o
    // backend faz DELETE-all + re-INSERT idempotente.
    const payload = {
      ...values,
      isVendedor: isVendedorLimpo,
      ...permissoes,
      profileId,
      cofreSegmentos,
      // CARD-VENDAS-VENDEDOR: null explícito REMOVE o vínculo no backend.
      omieVendedorCodigo,
      // ⚠️ Vazio vira null: "apaguei" e "nunca preenchi" são a mesma coisa aqui.
      telefoneWhatsapp: telefoneWhatsapp.trim() || null,
    };

    // C3 (rodada AJUDA-UX-QA): admin DESLIGOU o switch "Usuário ativo" de um
    // colaborador do departamento comercial → antes de salvar, decidir o
    // destino da carteira (leads/propostas/clientes). Nada é apagado.
    const estaInativando = isEdit && !!user && user.ativo && !values.ativo;
    const eraComercial = !!user?.departamentos.some((d) => d.departmentSlug === 'comercial');
    if (estaInativando && user && eraComercial) {
      let temCarteira: boolean | null = null;
      try {
        const resumo = await api.get<CarteiraResumo>(
          `/api/v1/crm/carteira/${encodeURIComponent(user.id)}`,
        );
        temCarteira =
          resumo.carteira.leads + resumo.carteira.cards + resumo.carteira.clientes > 0;
      } catch {
        temCarteira = null; // não conseguiu consultar — segue com confirm simples
      }

      if (temCarteira) {
        // Wizard assume daqui: o POST de offboarding já inativa o usuário.
        setOffboardingAberto(true);
        return;
      }

      const ok = await confirmar({
        title: `Inativar ${user.nome}?`,
        message:
          temCarteira === null
            ? `Não foi possível consultar a carteira de ${user.nome} agora. ${user.nome} perde o acesso, mas nada é apagado — o nome continua nos históricos. Inativar mesmo assim?`
            : `A carteira de ${user.nome} está vazia. ${user.nome} perde o acesso, mas nada é apagado — o nome continua nos históricos.`,
        confirmLabel: 'Inativar',
      });
      if (!ok) return;
    }

    // Sem catch, uma falha de API deixava o modal aberto SEM feedback nenhum
    // (createUser/updateUser re-lançam). Toast em pt-BR e mantém o modal aberto
    // pra nova tentativa.
    try {
      if (isEdit && user) {
        await updateUser(user.id, payload);
      } else {
        await createUser(payload);
      }
    } catch {
      toast.show(
        isEdit ? 'Não foi possível salvar as alterações.' : 'Não foi possível criar o usuário.',
        { variant: 'error' },
      );
      return;
    }
    onClose();
  }

  // Departamentos disponíveis pra adicionar (não selecionados ainda)
  const availableDepts = departments.filter(
    (d) => d.ativo && !fields.find((f) => f.departmentSlug === d.slug)
  );

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => !o && onClose()} size="lg">
      <DialogHeader>
        <DialogTitle>{isEdit ? 'Editar usuário' : 'Novo usuário'}</DialogTitle>
        <DialogDescription>
          {isEdit
            ? `Atualizar informações de ${user.nome}.`
            : 'Adicionar um novo usuário à plataforma. Um email de cadastro será enviado.'}
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent>
          <div className="space-y-5">
            {/* Nome + Email */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="nome" required>Nome completo</Label>
                <Input
                  id="nome"
                  autoFocus
                  autoComplete="name"
                  placeholder="João da Silva"
                  error={errors.nome?.message}
                  {...register('nome')}
                />
              </div>
              <div>
                <Label htmlFor="email" required>Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="joao@igmsecurity.com.br"
                  error={errors.email?.message}
                  {...register('email')}
                />
              </div>
            </div>

            {/* Status + Admin */}
            <div className="flex flex-col sm:flex-row gap-6 p-4 rounded-xl bg-paper-warm dark:bg-paper-dark-warm">
              <Controller
                name="ativo"
                control={control}
                render={({ field }) => (
                  <Switch
                    label="Usuário ativo"
                    hint="Desativar bloqueia o login sem excluir o usuário"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                )}
              />
              <Controller
                name="isAdmin"
                control={control}
                render={({ field }) => (
                  <Switch
                    label="Administrador"
                    hint="Acesso completo às configurações e às permissões de todo mundo"
                    checked={field.value}
                    onChange={(e) => {
                      field.onChange(e.target.checked);
                      // FEAT-V2-037: se desmarcar admin enquanto profileId=null,
                      // force pra 'p_administrativo' (super-admin só com admin=true).
                      if (!e.target.checked && profileId === null) {
                        setProfileId('p_administrativo');
                      }
                    }}
                  />
                )}
              />
            </div>

            {/* FEAT-V2-037 Fase A: seletor de perfil de uso. */}
            <Controller
              name="isAdmin"
              control={control}
              render={({ field }) => {
                const isAdminChecked = !!field.value;
                return (
                  <div data-testid="profile-selector" className="space-y-1">
                    <Label htmlFor="user-profile">Perfil de uso</Label>
                    <Select
                      id="user-profile"
                      value={profileId === null ? '__super_admin__' : profileId}
                      onChange={(e) => {
                        const v = e.target.value;
                        setProfileId(v === '__super_admin__' ? null : v);
                      }}
                    >
                      {profiles.map((p) => (
                          <option key={p.id} value={p.id}>
                            {PROFILE_SLUG_LABELS[p.slug as ProfileSlug] ?? p.nome}
                          </option>
                        ))}
                      {/* Super-admin só é opção quando isAdmin está marcado. */}
                      {isAdminChecked && (
                        <option value="__super_admin__">
                          Acesso total, sem perfil definido
                        </option>
                      )}
                    </Select>
                    <p className="mt-1 text-xs text-ink-2 dark:text-ink-dark-2">
                      É o ponto de partida dos acessos da pessoa. Os ajustes individuais abaixo
                      continuam valendo por cima. A opção de acesso total só aparece quando
                      Administrador está marcado.
                    </p>
                  </div>
                );
              }}
            />

            {/* Departamentos */}
            <div>
              <Label>Departamentos</Label>
              <p className="text-xs text-ink-3 dark:text-ink-dark-3 mb-3 -mt-1">
                Atribua o usuário a um ou mais departamentos com papel específico em cada.
              </p>

              {/* Copiar de outro usuário */}
              {otherUsers.length > 0 && (
                <div className="mb-3 p-3 rounded-xl bg-paper-warm dark:bg-paper-dark-warm flex flex-col sm:flex-row gap-2 sm:items-center">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-ink-1 dark:text-ink-dark-1">
                      Copiar departamentos de outro usuário
                    </p>
                    {copiedFromName ? (
                      <p className="mt-0.5 text-xs text-success">
                        Copiado de {copiedFromName}
                      </p>
                    ) : (
                      <p className="mt-0.5 text-xs text-ink-2 dark:text-ink-dark-2">
                        Substitui os departamentos atuais
                      </p>
                    )}
                  </div>
                  <div className="flex min-w-0 flex-wrap gap-2 sm:flex-shrink-0">
                    <Select
                      size="sm"
                      value={copyFromUserId}
                      onChange={(e) => setCopyFromUserId(e.target.value)}
                      className="min-w-0 max-w-full sm:min-w-[180px]"
                    >
                      <option value="">Selecione uma pessoa…</option>
                      {otherUsers.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.nome} ({u.departamentos.length}{' '}
                          {u.departamentos.length === 1 ? 'departamento' : 'departamentos'})
                        </option>
                      ))}
                    </Select>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={handleCopyDepartments}
                      disabled={!copyFromUserId}
                    >
                      Copiar
                    </Button>
                  </div>
                </div>
              )}

              {/* BF.5 / FEAT-V2-015: copiar aspectos avançados via backend.
                  Só em edit pq endpoint exige target existir. */}
              {isEdit && otherUsers.length > 0 && (
                <div
                  data-testid="copy-aspects-panel"
                  className="mb-3 p-3 rounded-xl bg-paper-warm dark:bg-paper-dark-warm space-y-2"
                >
                  <p className="text-xs font-medium text-ink-1 dark:text-ink-dark-1">
                    Copiar atividades / preferências de outro usuário
                  </p>
                  <Select
                    size="sm"
                    value={copyAspectsSource}
                    onChange={(e) => setCopyAspectsSource(e.target.value)}
                    data-testid="copy-aspects-source"
                  >
                    <option value="">Copiar de quem…</option>
                    {otherUsers.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.nome}
                      </option>
                    ))}
                  </Select>
                  <div className="flex flex-wrap gap-2">
                    {(Object.keys(COPY_ASPECT_LABELS) as CopyAspect[]).map((a) => (
                      <label
                        key={a}
                        className="inline-flex items-center gap-1.5 text-xs cursor-pointer"
                        data-testid={`copy-aspect-label-${a}`}
                      >
                        <input
                          type="checkbox"
                          checked={copyAspects.has(a)}
                          onChange={() => toggleAspect(a)}
                          data-testid={`copy-aspect-${a}`}
                          className="w-3.5 h-3.5 rounded text-red focus:ring-red"
                        />
                        {COPY_ASPECT_LABELS[a]}
                      </label>
                    ))}
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    {copyAspectsResult ? (
                      <p
                        className={`min-w-0 text-xs ${copyAspectsResult.ok ? 'text-success' : 'text-danger'}`}
                        data-testid="copy-aspects-result"
                      >
                        {copyAspectsResult.texto}
                      </p>
                    ) : (
                      <p className="min-w-0 text-xs text-ink-2 dark:text-ink-dark-2">
                        Não copia atividades concluídas nem responsável atual.
                      </p>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={handleCopyAspects}
                      disabled={!copyAspectsSource || copyAspects.size === 0 || copyAspectsPending}
                      loading={copyAspectsPending}
                      data-testid="copy-aspects-submit"
                    >
                      Copiar
                    </Button>
                  </div>
                </div>
              )}

              {fields.length === 0 ? (
                <div className="text-center py-6 px-4 rounded-xl border-2 border-dashed border-line dark:border-line-dark">
                  <p className="text-sm text-ink-3 dark:text-ink-dark-3 mb-3">
                    Nenhum departamento atribuído
                  </p>
                  {availableDepts.length > 0 && (
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() =>
                        append({ departmentSlug: availableDepts[0].slug, role: 'colaborador' })
                      }
                    >
                      Atribuir departamento
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {fields.map((f, index) => (
                    <div
                      key={f.id}
                      className="flex flex-wrap items-center gap-2 rounded-xl border border-line p-3 dark:border-line-dark"
                    >
                      <Controller
                        name={`departamentos.${index}.departmentSlug` as const}
                        control={control}
                        render={({ field }) => (
                          <Select
                            value={field.value}
                            onChange={(e) => field.onChange(e.target.value)}
                            className="min-w-0 flex-1"
                          >
                            {departments
                              .filter(
                                (d) =>
                                  d.slug === field.value ||
                                  !fields.find((ff) => ff.departmentSlug === d.slug)
                              )
                              .map((d) => (
                                <option key={d.slug} value={d.slug}>
                                  {d.nome}
                                </option>
                              ))}
                          </Select>
                        )}
                      />
                      <Controller
                        name={`departamentos.${index}.role` as const}
                        control={control}
                        render={({ field }) => (
                          <Select
                            value={field.value}
                            onChange={(e) => field.onChange(e.target.value as RoleType)}
                            className="w-44 max-w-full"
                          >
                            <option value="lider">{ROLES_LABELS.lider.label}</option>
                            <option value="colaborador">{ROLES_LABELS.colaborador.label}</option>
                            <option value="visualizador">{ROLES_LABELS.visualizador.label}</option>
                          </Select>
                        )}
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => remove(index)}
                        className="text-red"
                        aria-label="Remover departamento"
                      >
                        Remover
                      </Button>
                    </div>
                  ))}

                  {availableDepts.length > 0 && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        append({ departmentSlug: availableDepts[0].slug, role: 'colaborador' })
                      }
                    >
                      + Adicionar departamento
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* INDICADORES-REDESIGN (Migration 382): marcador de vendedor.
                Só aparece pra quem tem o departamento comercial selecionado
                (recalculado ao vivo). Quem não é vendedor segue com acesso
                normal ao Comercial, só não conta em metas/ranking/rateio. */}
            {temComercial && (
              <div
                data-testid="vendedor-switch"
                className="flex items-start gap-2 p-4 rounded-xl bg-paper-warm dark:bg-paper-dark-warm"
              >
                <Controller
                  name="isVendedor"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      label="Vendedor — participa de metas e ranking"
                      hint="Entra no ranking de vendedores, nas metas por vendedor e no rateio da meta de equipe."
                      checked={!!field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  )}
                />
                <HelpTip texto="Marque para quem realmente vende. Esse colaborador passa a contar no ranking de vendedores, nas metas por vendedor e no rateio das metas de equipe. Quem fica desmarcado continua com acesso normal ao Comercial (abre propostas, cuida de leads), só não aparece nesses números — é o caso de orçamentista, apoio ou gestor que atuam no comercial mas não têm meta individual." />
              </div>
            )}

            {/* CARD-VENDAS-VENDEDOR (Migration 491): vínculo usuário ↔
                vendedor do Omie. NÃO depende do departamento comercial — o
                Enzo (clientes) e o Gabriel (compras) vendem sem ser do
                comercial. '' = sem vínculo → o card "Minhas vendas" não
                aparece na Home da pessoa (e não mostra zero). */}
            <div
              data-testid="whatsapp-do-colaborador"
              className="p-4 rounded-xl bg-paper-warm dark:bg-paper-dark-warm space-y-2"
            >
              <h4 className="text-sm font-semibold text-ink-1 dark:text-ink-dark-1 inline-flex items-center gap-1.5">
                WhatsApp desta pessoa
                <HelpTip texto="Serve para UMA coisa só: o robô ignorar as mensagens desta pessoa nos grupos. Sem isso, um técnico que escrevesse no grupo de um condomínio de madrugada receberia a resposta automática de fora do horário, como se fosse o síndico perguntando. Não é obrigatório, e ficha em branco não atrapalha nada — quem não tem número cadastrado simplesmente não é reconhecido. Escreva como preferir: o sistema compara só o DDD e os últimos oito dígitos, porque o número que chega do WhatsApp nunca vem escrito igual ao que você digitou. Este campo NÃO manda mensagem, não recupera senha e não vira notificação — para escolher como a pessoa recebe avisos, o lugar é Perfil › Notificações." />
              </h4>
              <p className="text-xs text-ink-3 dark:text-ink-dark-3">
                Opcional. O robô ignora as mensagens deste número nos grupos — e não faz mais nada
                com ele.
              </p>
              <Input
                id="user-whatsapp"
                value={telefoneWhatsapp}
                onChange={(e) => setTelefoneWhatsapp(e.target.value)}
                placeholder="(19) 99123-4567"
                inputMode="tel"
                autoComplete="off"
                data-testid="user-whatsapp-input"
              />
            </div>

            <div
              data-testid="vendedor-omie-section"
              className="p-4 rounded-xl bg-paper-warm dark:bg-paper-dark-warm space-y-2"
            >
              <h4 className="text-sm font-semibold text-ink-1 dark:text-ink-dark-1 inline-flex items-center gap-1.5">
                Vendedor no Omie
                <HelpTip texto="Liga esta pessoa ao cadastro de vendedor do Omie. Com o vínculo, a tela inicial dela mostra o card 'Minhas vendas' — o total que ELA vendeu no mês vigente (cinco categorias de venda, pela data de emissão, somando as duas empresas). Sem vínculo, o card não aparece. O vínculo guarda o código do vendedor, então renomear o vendedor no Omie não quebra nada." />
              </h4>
              <p className="text-xs text-ink-3 dark:text-ink-dark-3">
                Escolha quem esta pessoa é no cadastro de vendedores do Omie. Sem vínculo, o card
                &quot;Minhas vendas&quot; não aparece na tela inicial dela.
              </p>
              <Select
                value={omieVendedorCodigo ?? ''}
                onChange={(e) => setOmieVendedorCodigo(e.target.value || null)}
                data-testid="vendedor-omie-select"
              >
                <option value="">Sem vínculo — o card não aparece</option>
                {/* Vínculo gravado com um código que não está na lista (ex.:
                    o código da OUTRA empresa, ou vendedor removido do Omie):
                    mostra o código cru em vez de fingir "sem vínculo". */}
                {omieVendedorCodigo && !vendedoresOmie.some((v) => v.codigo === omieVendedorCodigo) && (
                  <option value={omieVendedorCodigo}>{`Código ${omieVendedorCodigo} (fora da lista atual)`}</option>
                )}
                {vendedoresOmie.map((v) => (
                  <option key={v.codigo} value={v.codigo}>
                    {v.nome}
                  </option>
                ))}
              </Select>
            </div>

            {/* CA4 (Migration 406) + P1 (AJUSTES-POS-2): acesso ao cofre por
                segmento. Agora SEMPRE visível (descobrível), com aviso de que só
                afeta quem entra no cofre (dept Manutenção/Projetos). Quando o
                usuário ganha um desses departamentos aqui no modal, o aviso some
                ao vivo (temCofreDept via useWatch) — sem salvar+reabrir. */}
            <div
              data-testid="cofre-segmentos-section"
              className="p-4 rounded-xl bg-paper-warm dark:bg-paper-dark-warm space-y-3"
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h4 className="text-sm font-semibold text-ink-1 dark:text-ink-dark-1 inline-flex items-center gap-1">
                    Acesso ao cofre por segmento
                    <HelpTip texto="Limite quais segmentos de cliente este usuário vê no cofre de acessos e senhas. Sem nenhum segmento marcado, ele acessa o cofre de todos os clientes (padrão). Ao marcar segmentos, ele passa a ver só o cofre dos clientes que tenham pelo menos um deles. Só afeta quem entra no cofre (Manutenção e Projetos). Administradores sempre acessam todos." />
                  </h4>
                  <p className="text-xs text-ink-3 dark:text-ink-dark-3 mt-0.5">
                    Segmento é a marcação (tag) do cliente. Sem seleção = acesso a todos.
                  </p>
                </div>
              </div>

              {isAdminAtual ? (
                <p className="text-xs text-ink-3 dark:text-ink-dark-3">
                  Este usuário é administrador e acessa o cofre de todos os segmentos. A
                  restrição por segmento não se aplica.
                </p>
              ) : segmentosDisponiveis.length === 0 ? (
                <p className="text-xs text-ink-3 dark:text-ink-dark-3">
                  Nenhum segmento cadastrado ainda. Os segmentos chegam pela sincronização do
                  Omie; depois você restringe o acesso por aqui.
                </p>
              ) : (
                <>
                  {!temCofreDept && (
                    <p
                      className="text-xs text-ink-3 dark:text-ink-dark-3"
                      data-testid="cofre-segmentos-aviso-sem-dept"
                    >
                      Só afeta quem entra no cofre — hoje os departamentos <strong>Manutenção</strong> e{' '}
                      <strong>Projetos</strong>. Este usuário ainda não está em nenhum deles, então a
                      restrição abaixo passa a valer quando você adicionar um desses departamentos acima.
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {segmentosDisponiveis.map((seg) => (
                      <label
                        key={seg}
                        className="inline-flex items-center gap-1.5 text-xs cursor-pointer rounded-lg border border-line dark:border-line-dark px-2.5 py-1.5"
                        data-testid={`cofre-segmento-label-${seg}`}
                      >
                        <input
                          type="checkbox"
                          checked={cofreSegmentos.includes(seg)}
                          onChange={() => toggleCofreSegmento(seg)}
                          data-testid={`cofre-segmento-${seg}`}
                          className="w-3.5 h-3.5 rounded text-red focus:ring-red"
                        />
                        {seg}
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-ink-3 dark:text-ink-dark-3">
                    {cofreSegmentos.length === 0
                      ? 'Sem restrição: acessa o cofre de todos os segmentos.'
                      : `Acessa só o cofre dos clientes com: ${cofreSegmentos.join(', ')}.`}
                  </p>
                </>
              )}
            </div>

            {!isEdit && (
              <Alert variant="info" rail>
                <strong>Acesso:</strong> o usuário é criado agora. Ele entra na plataforma pelo
                login com a conta Google do email informado — no primeiro acesso, os dados reais
                (nome e foto) são preenchidos automaticamente.
              </Alert>
            )}

            {/* UI-2 / TD-V2-PERM-02 + FEAT-V2-035: seção Permissões. */}
            <UserPermissoesSection
              value={permissoes}
              onChange={setPermissoes}
              candidatosAprovador={otherUsers}
              isEditingAdmin={user?.isAdmin === true}
            />

            {/* TD-V2-UX-01 (2026-05-26): seção pra admin copiar prefs de
                outro user. Só em modo edit (precisa de targetUserId). */}
            {isEdit && user && (
              <UserNotificationCopySection targetUserId={user.id} />
            )}
          </div>
        </DialogContent>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button type="submit" loading={isSubmitting || pending === 'create' || (isEdit && pending === user?.id)}>
            {isEdit ? 'Salvar alterações' : 'Criar usuário'}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>

    {/* C3: wizard de offboarding — abre por cima do form quando o admin
        inativa um colaborador do comercial com carteira. O POST do wizard
        já inativa o usuário; ao concluir, fecha wizard E form. */}
    {isEdit && user && (
      <OffboardingCarteiraWizard
        open={offboardingAberto}
        userId={user.id}
        userNome={user.nome}
        onClose={() => setOffboardingAberto(false)}
        onDone={() => {
          setOffboardingAberto(false);
          onClose();
        }}
      />
    )}
    </>
  );
}
